import { type Block, type Deck, type Reference, type Section, type Slide, flattenDeck } from "@hali-shares/deck-schema";
import { hostOf } from "@hali-shares/deck-schema";

export { hostOf };

export type FlowBlock = Extract<Block, { type: "flow" }>;
export type ScreenshotBlock = Extract<Block, { type: "screenshot" }>;
export type PollBlock = Extract<Block, { type: "poll" }>;
export type QuizBlock = Extract<Block, { type: "quiz" }>;
export type ConceptBlock = Extract<Block, { type: "concept" }>;
export type ReferencesBlock = Extract<Block, { type: "references" }>;
export type VideoBlock = Extract<Block, { type: "video" }>;
export type TimelineBlock = Extract<Block, { type: "timeline" }>;

/** Everything the presenter needs to resolve a slide, computed once per deck. */
export type DeckModel = {
  deck: Deck;
  slides: Slide[];
  blocks: Map<string, { block: Block; section: Section; sectionNumber: number }>;
  sections: Map<string, { section: Section; number: number }>;
  /** Footnote number for each reference: its position in deck.references, from 1. */
  refNumber: Map<string, number>;
  refs: Map<string, Reference>;
};

export function buildModel(deck: Deck): DeckModel {
  const blocks: DeckModel["blocks"] = new Map();
  const sections: DeckModel["sections"] = new Map();
  deck.sections.forEach((section, i) => {
    sections.set(section.id, { section, number: i + 1 });
    section.blocks.forEach((block) => blocks.set(block.id, { block, section, sectionNumber: i + 1 }));
  });
  return {
    deck,
    slides: flattenDeck(deck),
    blocks,
    sections,
    refNumber: new Map(deck.references.map((r, i) => [r.id, i + 1])),
    refs: new Map(deck.references.map((r) => [r.id, r])),
  };
}

/**
 * Nodes of a flow that are zoom stops, in node order. Stop n (1 ≤ n ≤ length) is detailNodes[n - 1]; the stop
 * after the last zoom is the overview again.
 */
export function detailNodes(flow: FlowBlock) {
  return flow.nodes.filter((n) => n.detail);
}

export function blockTitle(block: Block): string {
  switch (block.type) {
    case "poll":
    case "quiz":
      return block.question;
    default:
      return block.title;
  }
}

export function slideTitle(model: DeckModel, slide: Slide): string {
  if (slide.kind === "title") return model.deck.title;
  if (slide.kind === "section") return model.sections.get(slide.sectionId)?.section.title ?? "";
  const entry = model.blocks.get(slide.blockId);
  return entry ? blockTitle(entry.block) : "";
}

/** "The agent loop › Act" when zoomed into a node; the plain slide title otherwise. */
export function nowTitle(model: DeckModel, slideIndex: number, stop: number): string {
  const slide = model.slides[slideIndex];
  if (!slide) return "";
  const title = slideTitle(model, slide);
  if (slide.kind !== "block" || stop === 0) return title;
  const block = model.blocks.get(slide.blockId)?.block;
  if (block?.type === "flow") {
    const node = detailNodes(block)[stop - 1];
    return node ? `${title} › ${node.label}` : title;
  }
  if (block?.type === "screenshot") return `${title} › Question ${stop}`;
  if (block?.type === "timeline") {
    const item = block.items[stop];
    return item ? `${title} › ${item.label}` : title;
  }
  return title;
}

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${d} ${months[(m ?? 1) - 1]} ${y}`;
}

export function referenceMeta(r: Reference): string {
  const venueYear = [r.venue, r.year].filter(Boolean).join(" ");
  return [r.authors, venueYear].filter(Boolean).join(" · ");
}
